import { YearMonthPoint } from "./dashboardAggregations";

// ------------------------------------------------------------------
// Forecast module
//
// Two algorithms side-by-side, then ensembled:
//
//   Holt-Winters additive
//   ---------------------
//     level_t  = α(y_t − s_{t−L}) + (1−α)(level_{t−1} + trend_{t−1})
//     trend_t  = β(level_t − level_{t−1}) + (1−β) trend_{t−1}
//     season_t = γ(y_t − level_t) + (1−γ) season_{t−L}
//     ŷ_{t+h} = level_t + h·trend_t + s_{(t+h) mod L − L}
//
//   Seasonal naive with growth factor
//   ----------------------------------
//     ŷ_{t+h} = y_{t+h−L} · g
//     where g = Σ y_{last L} / Σ y_{prev L}  (ratio of last year / previous year)
//
// Prediction intervals use the residual standard deviation of the
// method's in-sample fit, assuming normality. For small samples this is
// an approximation (see TODOS — MAPE backtest would sharpen this).
//
// Data volume: ≤ 60 monthly points per series × 7 metrics. A grid
// search over smoothing parameters is cheap and defensible.
// ------------------------------------------------------------------

export const SEASON_LENGTH = 12;
export const DEFAULT_HORIZON = 12;
export const MIN_SEASONS_FOR_HW = 2;    // Need 2 full cycles for additive HW to be meaningful.
export const MIN_POINTS_FOR_SN = SEASON_LENGTH;

export interface ForecastPoint {
    year: number;
    monthIndex: number;
    key: string;
    mean: number;
    lower80: number;
    upper80: number;
    lower95: number;
    upper95: number;
}

export interface ForecastResult {
    history: YearMonthPoint[];
    hw: ForecastPoint[] | null;     // null when insufficient data
    sn: ForecastPoint[] | null;     // null when insufficient data
    ensemble: ForecastPoint[];       // always present if any method succeeded
    method: "hw+sn" | "hw" | "sn" | "none";
    trainedThrough: string | null;   // YYYY-MM of last training point, or null
    insufficientReason?: string;
}

// ------------------------------------------------------------------
// Utilities
// ------------------------------------------------------------------

export function hasEnoughHistory(series: YearMonthPoint[], minSeasons: number = MIN_SEASONS_FOR_HW): boolean {
    // Dense series required. Gaps would break seasonal fitting.
    if (series.length < minSeasons * SEASON_LENGTH) return false;
    if (!isDenseContiguous(series)) return false;
    return true;
}

export function isDenseContiguous(series: YearMonthPoint[]): boolean {
    if (series.length < 2) return series.length === 1;
    for (let i = 1; i < series.length; i++) {
        const prev = series[i - 1];
        const cur = series[i];
        const expectedYear = cur.monthIndex === 0 ? prev.year + 1 : prev.year;
        const expectedMonth = cur.monthIndex === 0 ? 0 : prev.monthIndex + 1;
        if (cur.year !== expectedYear || cur.monthIndex !== expectedMonth) return false;
    }
    return true;
}

function advanceYearMonth(year: number, monthIndex: number, steps: number): { year: number; monthIndex: number } {
    const total = year * 12 + monthIndex + steps;
    return { year: Math.floor(total / 12), monthIndex: total % 12 };
}

function ymKey(year: number, monthIndex: number): string {
    return `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
}

function round(n: number): number {
    return Number.isFinite(n) ? Math.round(n) : 0;
}

function stdev(arr: number[]): number {
    if (arr.length === 0) return 0;
    const mean = arr.reduce((s, v) => s + v, 0) / arr.length;
    const variance = arr.reduce((s, v) => s + (v - mean) ** 2, 0) / Math.max(1, arr.length - 1);
    return Math.sqrt(variance);
}

function zClamp(p: 80 | 95): number {
    // Standard normal quantiles.
    return p === 80 ? 1.2816 : 1.96;
}

// ------------------------------------------------------------------
// Holt-Winters additive
// ------------------------------------------------------------------

interface HWFit {
    level: number;
    trend: number;
    seasons: number[];   // length = SEASON_LENGTH
    fitted: number[];    // same length as input series
    residuals: number[];
}

function fitHoltWintersAdditive(
    values: number[],
    alpha: number,
    beta: number,
    gamma: number,
): HWFit | null {
    const L = SEASON_LENGTH;
    if (values.length < 2 * L) return null;

    // Seed level = mean of first season, trend = (mean(second season) - mean(first season)) / L.
    const firstSeason = values.slice(0, L);
    const secondSeason = values.slice(L, 2 * L);
    const mean1 = firstSeason.reduce((s, v) => s + v, 0) / L;
    const mean2 = secondSeason.reduce((s, v) => s + v, 0) / L;
    let level = mean1;
    let trend = (mean2 - mean1) / L;
    const seasons: number[] = firstSeason.map(v => v - mean1);

    const fitted: number[] = new Array(values.length).fill(0);
    for (let t = 0; t < L; t++) {
        fitted[t] = level + seasons[t];
    }

    for (let t = L; t < values.length; t++) {
        const prevLevel = level;
        const prevTrend = trend;
        const prevSeason = seasons[t % L];

        level = alpha * (values[t] - prevSeason) + (1 - alpha) * (prevLevel + prevTrend);
        trend = beta * (level - prevLevel) + (1 - beta) * prevTrend;
        seasons[t % L] = gamma * (values[t] - level) + (1 - gamma) * prevSeason;

        // In-sample 1-step-ahead forecast for residuals.
        fitted[t] = prevLevel + prevTrend + prevSeason;
    }

    const residuals: number[] = [];
    for (let t = L; t < values.length; t++) {
        residuals.push(values[t] - fitted[t]);
    }

    return { level, trend, seasons, fitted, residuals };
}

function sseOfFit(values: number[], fit: HWFit | null): number {
    if (!fit) return Infinity;
    let sse = 0;
    for (let i = 0; i < fit.residuals.length; i++) sse += fit.residuals[i] ** 2;
    return sse;
}

export function holtWintersAdditive(
    series: YearMonthPoint[],
    horizon: number = DEFAULT_HORIZON,
): ForecastPoint[] | null {
    if (!hasEnoughHistory(series, MIN_SEASONS_FOR_HW)) return null;

    const values = series.map(p => p.value);

    // Coarse grid search over (α, β, γ) ∈ (0, 1). Small grid — 36×36×... blows up,
    // so keep it 7^3 = 343 evaluations. Plenty for short series.
    const grid = [0.05, 0.15, 0.3, 0.5, 0.7, 0.85, 0.95];
    let best: HWFit | null = null;
    let bestSSE = Infinity;
    for (const a of grid) {
        for (const b of grid) {
            for (const g of grid) {
                const fit = fitHoltWintersAdditive(values, a, b, g);
                if (!fit) continue;
                const sse = sseOfFit(values, fit);
                if (sse < bestSSE) {
                    bestSSE = sse;
                    best = fit;
                }
            }
        }
    }
    if (!best) return null;

    const sigma = stdev(best.residuals);
    const last = series[series.length - 1];
    const out: ForecastPoint[] = [];
    for (let h = 1; h <= horizon; h++) {
        const { year, monthIndex } = advanceYearMonth(last.year, last.monthIndex, h);
        const seasonIdx = (series.length + h - 1) % SEASON_LENGTH;
        const mean = best.level + h * best.trend + best.seasons[seasonIdx];
        // Prediction interval widens with horizon (naive: sigma*sqrt(h)).
        const width80 = zClamp(80) * sigma * Math.sqrt(h);
        const width95 = zClamp(95) * sigma * Math.sqrt(h);
        out.push({
            year,
            monthIndex,
            key: ymKey(year, monthIndex),
            mean: round(mean),
            lower80: round(mean - width80),
            upper80: round(mean + width80),
            lower95: round(mean - width95),
            upper95: round(mean + width95),
        });
    }
    return out;
}

// ------------------------------------------------------------------
// Seasonal naive with growth factor
// ------------------------------------------------------------------

export function seasonalNaive(
    series: YearMonthPoint[],
    horizon: number = DEFAULT_HORIZON,
): ForecastPoint[] | null {
    if (series.length < MIN_POINTS_FOR_SN) return null;
    if (!isDenseContiguous(series)) return null;

    const values = series.map(p => p.value);
    const L = SEASON_LENGTH;

    // Growth factor: last L vs prior L (if we have 2 full seasons).
    // Otherwise 1.0 (pure last-year mirror).
    let growth = 1.0;
    if (values.length >= 2 * L) {
        const lastSum = values.slice(-L).reduce((s, v) => s + v, 0);
        const prevSum = values.slice(-2 * L, -L).reduce((s, v) => s + v, 0);
        if (prevSum > 0) growth = lastSum / prevSum;
    }

    // In-sample residuals: predict each y_t from y_{t-L} * local-growth.
    // For residuals we use flat growth=1 to avoid circular dependence.
    const residuals: number[] = [];
    for (let t = L; t < values.length; t++) {
        residuals.push(values[t] - values[t - L]);
    }
    const sigma = stdev(residuals);

    const last = series[series.length - 1];
    const out: ForecastPoint[] = [];
    for (let h = 1; h <= horizon; h++) {
        const { year, monthIndex } = advanceYearMonth(last.year, last.monthIndex, h);
        // Index in original series of the same month one year back.
        const baseIdx = values.length - L + (h - 1);
        const base = baseIdx >= 0 && baseIdx < values.length ? values[baseIdx] : values[values.length - 1];
        const mean = base * growth;
        const width80 = zClamp(80) * sigma * Math.sqrt(Math.ceil(h / L));
        const width95 = zClamp(95) * sigma * Math.sqrt(Math.ceil(h / L));
        out.push({
            year,
            monthIndex,
            key: ymKey(year, monthIndex),
            mean: round(mean),
            lower80: round(mean - width80),
            upper80: round(mean + width80),
            lower95: round(mean - width95),
            upper95: round(mean + width95),
        });
    }
    return out;
}

// ------------------------------------------------------------------
// Ensemble — mean of means, widened band = superset of both bands.
// ------------------------------------------------------------------

export function ensembleForecast(hw: ForecastPoint[] | null, sn: ForecastPoint[] | null): ForecastPoint[] {
    if (hw && sn) {
        const out: ForecastPoint[] = [];
        const n = Math.min(hw.length, sn.length);
        for (let i = 0; i < n; i++) {
            const h = hw[i];
            const s = sn[i];
            out.push({
                year: h.year,
                monthIndex: h.monthIndex,
                key: h.key,
                mean: round((h.mean + s.mean) / 2),
                lower80: Math.min(h.lower80, s.lower80),
                upper80: Math.max(h.upper80, s.upper80),
                lower95: Math.min(h.lower95, s.lower95),
                upper95: Math.max(h.upper95, s.upper95),
            });
        }
        return out;
    }
    return (hw ?? sn ?? []);
}

// ------------------------------------------------------------------
// Public entry point
// ------------------------------------------------------------------

export interface RunForecastOptions {
    horizon?: number;
    minSeasons?: number;
}

export function runForecast(
    series: YearMonthPoint[],
    opts: RunForecastOptions = {},
): ForecastResult {
    const horizon = opts.horizon ?? DEFAULT_HORIZON;
    const minSeasons = opts.minSeasons ?? MIN_SEASONS_FOR_HW;

    if (series.length === 0) {
        return {
            history: [],
            hw: null,
            sn: null,
            ensemble: [],
            method: "none",
            trainedThrough: null,
            insufficientReason: "Ingen historik tilgængelig.",
        };
    }

    const last = series[series.length - 1];
    const trainedThrough = ymKey(last.year, last.monthIndex);

    if (!isDenseContiguous(series)) {
        return {
            history: series,
            hw: null,
            sn: null,
            ensemble: [],
            method: "none",
            trainedThrough,
            insufficientReason: "Historikken er ikke sammenhængende (manglende måneder).",
        };
    }

    const hw = hasEnoughHistory(series, minSeasons) ? holtWintersAdditive(series, horizon) : null;
    const sn = series.length >= MIN_POINTS_FOR_SN ? seasonalNaive(series, horizon) : null;
    const ensemble = ensembleForecast(hw, sn);

    let method: ForecastResult["method"] = "none";
    if (hw && sn) method = "hw+sn";
    else if (hw) method = "hw";
    else if (sn) method = "sn";

    const reason = method === "none"
        ? `Ikke nok historik (${series.length} måneder). Kræver mindst ${MIN_POINTS_FOR_SN} for sæsonal-naiv og ${minSeasons * SEASON_LENGTH} for Holt-Winters.`
        : undefined;

    return {
        history: series,
        hw,
        sn,
        ensemble,
        method,
        trainedThrough,
        insufficientReason: reason,
    };
}

// ------------------------------------------------------------------
// KPI summary helpers — power the hero cards in ForecastView.
// ------------------------------------------------------------------

export type ConfidenceBucket = "Høj" | "Middel" | "Lav";

export interface ForecastSummary {
    /** Sum of ensemble.mean over the forecast horizon. */
    forecastTotal: number;
    /** Average width of the 80% band across the horizon (± value). */
    bandHalfWidth: number;
    /** Sum of actuals over the same number of most-recent history months. */
    historyTotal: number;
    /** YoY growth as a ratio; 0.08 = +8%. null when historyTotal is 0. */
    yoyDelta: number | null;
    /** Qualitative confidence based on band-width / mean. */
    confidence: ConfidenceBucket;
    /** horizon in months used for the totals. */
    horizon: number;
}

// z-score for 80% interval — keep in sync with forecast algorithms above.
const Z_80 = 1.2816;

/**
 * Compute headline numbers from a ForecastResult.
 *
 * Uncertainty of a sum of forecasts adds in QUADRATURE, not linearly:
 *   σ_total = √(Σ σ_month²)   and   halfWidth = z · σ_total
 * Previous version did halfWidth_total = avgMonthlyHalfWidth × horizon,
 * which overstated the total band by ~√horizon (≈ 3.5× at h=12).
 * The correct formula assumes month-to-month forecast errors are
 * approximately independent — which is a cleaner overstatement than
 * fully-correlated linear summation and a better default than either.
 */
export function summarizeForecast(result: ForecastResult): ForecastSummary | null {
    if (result.ensemble.length === 0) return null;
    const horizon = result.ensemble.length;

    let forecastTotal = 0;
    let totalVariance = 0;
    for (const p of result.ensemble) {
        forecastTotal += p.mean;
        const monthHalfWidth = (p.upper80 - p.lower80) / 2;
        const monthSigma = monthHalfWidth / Z_80;
        totalVariance += monthSigma * monthSigma;
    }
    const totalSigma = Math.sqrt(totalVariance);
    const totalHalfWidth = Math.round(Z_80 * totalSigma);

    const recentHistory = result.history.slice(-horizon);
    const historyTotal = recentHistory.reduce((s, p) => s + p.value, 0);

    const yoyDelta = historyTotal > 0
        ? (forecastTotal - historyTotal) / historyTotal
        : null;

    // Confidence bucket: band-width as a fraction of forecast.
    const relativeBand = forecastTotal > 0 ? totalHalfWidth / forecastTotal : 1;
    const confidence: ConfidenceBucket =
        relativeBand < 0.10 ? "Høj" :
        relativeBand < 0.25 ? "Middel" :
        "Lav";

    return {
        forecastTotal: Math.round(forecastTotal),
        bandHalfWidth: totalHalfWidth,
        historyTotal: Math.round(historyTotal),
        yoyDelta,
        confidence,
        horizon,
    };
}

/**
 * Summarise a specific CALENDAR YEAR as (actuals YTD) + (forecast for the
 * rest of the year). Used for the "Omsætning i år" hero KPI.
 *
 * - forecastTotal = sum of actuals for months already complete + sum of
 *   ensemble means for months still ahead.
 * - bandHalfWidth = uncertainty from the forecast portion only (actuals
 *   contribute zero uncertainty). Variances added in quadrature.
 * - historyTotal = actuals for the SAME calendar months of the PREVIOUS year
 *   (e.g. comparing 2026 to 2025 full year). Used for YoY delta.
 *
 * Returns null when no history or forecast data covers the requested year.
 */
export function summarizeForecastForYear(result: ForecastResult, year: number): ForecastSummary | null {
    // Collect this-year months from history.
    let yearTotal = 0;
    let monthsCounted = 0;
    for (const p of result.history) {
        if (p.year === year) {
            yearTotal += p.value;
            monthsCounted++;
        }
    }

    // Add forecast months that fall in the same year.
    let totalVariance = 0;
    for (const p of result.ensemble) {
        if (p.year === year) {
            yearTotal += p.mean;
            monthsCounted++;
            const monthHalfWidth = (p.upper80 - p.lower80) / 2;
            const monthSigma = monthHalfWidth / Z_80;
            totalVariance += monthSigma * monthSigma;
        }
    }

    if (monthsCounted === 0) return null;

    const totalSigma = Math.sqrt(totalVariance);
    const totalHalfWidth = Math.round(Z_80 * totalSigma);

    // Previous-year total from history.
    let prevYearTotal = 0;
    for (const p of result.history) {
        if (p.year === year - 1) prevYearTotal += p.value;
    }

    const yoyDelta = prevYearTotal > 0
        ? (yearTotal - prevYearTotal) / prevYearTotal
        : null;

    const relativeBand = yearTotal > 0 ? totalHalfWidth / yearTotal : 1;
    const confidence: ConfidenceBucket =
        relativeBand < 0.10 ? "Høj" :
        relativeBand < 0.25 ? "Middel" :
        "Lav";

    return {
        forecastTotal: Math.round(yearTotal),
        bandHalfWidth: totalHalfWidth,
        historyTotal: Math.round(prevYearTotal),
        yoyDelta,
        confidence,
        horizon: monthsCounted,
    };
}

